import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Flame, Volume2, VolumeX } from "lucide-react";
import { api } from "../lib/api";
import { fuzzyMatch } from "../lib/match";
import { applyMedResult, updateMissedRhythms } from "../lib/sr";
import { qrsBeep, startAlarm, stopAlarm, setMuted, isMuted, unlockAudio, isAudioUnlocked } from "../lib/audio";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { MonitorStrip } from "../components/MonitorStrip";
import { TwelveLeadGrid } from "../components/TwelveLeadGrid";
import { ScoreCard } from "../components/ScoreCard";
import { hrLabel, isPulseless } from "../lib/ekg";
import { recordSession } from "../lib/sessions";
import { awardCoins } from "../lib/coins";
import { useMuteHotkey } from "../lib/useMuteHotkey";
import { guestId, readGuestDaily, writeGuestDaily, guestStreak } from "../lib/guest";
import { useModality } from "../context/ModalityContext";
import { StudyTag } from "../components/ModalitySwitch";
import { StudySummary } from "../components/StudySummary";

const ITEM_SECONDS = 12;
const BASE_POINTS = 10;

export default function DailyChallenge() {
  const { user } = useAuth();
  const { isGuest, update } = useProgress();
  const { isStudy } = useModality();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("intro"); // intro | playing | done | studyDone
  const [result, setResult] = useState(null);
  const [studyResult, setStudyResult] = useState(null);
  const [runStudy, setRunStudy] = useState(false); // modality frozen at run start

  // Keep the idle screen in sync when the modality is toggled (never during a run)
  useEffect(() => {
    if (!data || stage === "playing") return;
    if (isStudy && stage === "done") setStage("intro");
    if (!isStudy && data.result && stage !== "done") {
      setResult({ ...data.result, streak: data.streak });
      setStage("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudy]);

  useEffect(() => {
    if (user === null) return;
    const params = user === false ? { guest_id: guestId() } : {};
    api.get("/daily", { params }).then((r) => {
      const d = r.data;
      if (d.guest) {
        const saved = readGuestDaily();
        d.streak = guestStreak(new Set(Object.keys(saved)), d.date);
        d.result = saved[d.date] ? { ...saved[d.date], streak: d.streak } : null;
      }
      setData(d);
      if (d.result && !isStudy) {
        setResult({ ...d.result, streak: d.streak });
        setStage("done");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveResult = async (summary) => {
    update((prev) => ({ stats: awardCoins(prev.stats || {}, summary.score, runStudy ? "daily study" : "daily challenge") }));
    if (runStudy) return summary;
    if (isGuest) {
      const saved = readGuestDaily();
      const entry = { ...summary, date: data.date, completed_at: new Date().toISOString() };
      saved[data.date] = entry;
      writeGuestDaily(saved);
      return { ...entry, streak: guestStreak(new Set(Object.keys(saved)), data.date) };
    }
    const { data: saved } = await api.post("/daily/result", summary);
    recordSession({ kind: "daily", category: "daily", mode: "any", score: summary.score });
    return saved;
  };

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-10 text-muted-foreground">Loading today's mix…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      {stage === "intro" && (
        <Intro data={data} isGuest={isGuest} study={isStudy} onStart={() => { setRunStudy(isStudy); setStage("playing"); }} />
      )}
      {stage === "playing" && (
        <DailyRunner
          items={data.items}
          study={runStudy}
          onFinish={async (summary) => {
            if (runStudy) {
              await saveResult(summary);
              setStudyResult(summary);
              setStage("studyDone");
              return;
            }
            const saved = await saveResult(summary);
            setResult(saved);
            setData((d) => ({ ...d, result: saved, streak: saved.streak ?? d.streak }));
            setStage("done");
          }}
        />
      )}
      {stage === "studyDone" && studyResult && (
        <StudySummary
          kicker={`DAILY MIX · ${data.date}`}
          attempted={studyResult.total}
          missed={studyResult.missedItems.map((it) => (it.type === "med"
            ? { id: it.id, prompt: it.clue, answer: it.generic }
            : { id: it.id, prompt: `Strip · ${hrLabel(it.params)} bpm`, answer: it.name, detail: it.explanation }))}
          onRestart={() => setStage("intro")}
          restartLabel="Study again"
          testid="daily-study-summary"
        />
      )}
      {stage === "done" && result && (
        <div data-testid="daily-done">
          <div className="mb-8">
            <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// DAILY CHALLENGE · {result.date}</div>
            <h1 className="text-4xl font-extrabold tracking-tight">
              Today's run is <span className="text-phosphor">logged</span>.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              A fresh mix drops at midnight UTC. Share your card, then review your misses in Flashcards.
              {isGuest && <span className="text-amber-300"> Guest results stay on this device — sign in to keep your streak everywhere.</span>}
            </p>
          </div>
          <ScoreCard result={result} name={user?.name} />
          <div className="mt-6 flex gap-3">
            <Link to="/flashcards" data-testid="daily-to-flashcards" className="border border-phosphor/50 px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/10">
              Review misses
            </Link>
            <Link to="/dashboard" className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/90">
              Dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Intro({ data, isGuest, study, onStart }) {
  const meds = data.items.filter((i) => i.type === "med").length;
  const rhythms = data.items.length - meds;
  return (
    <div data-testid="daily-intro">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// DAILY CHALLENGE · {data.date}{study && <StudyTag testid="daily-study-tag" />}</div>
        <h1 className="text-4xl font-extrabold tracking-tight">
          {study ? <>Today's mix. <span className="text-sky-300">No clock.</span></> : <>One shot. <span className="text-phosphor">Timed mix.</span></>}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          {study
            ? `${meds} medication clues and ${rhythms} live rhythms. Take your time — miss one and the answer appears so you can lock it in. Study runs don't touch your daily streak or score card${data.result ? " (today's competitive run is already logged)" : ""}.`
            : `${meds} medication clues and ${rhythms} live rhythms, shuffled just for you. Each one gives you ${ITEM_SECONDS} seconds — faster answers earn a bigger bonus. You only get one attempt per day.`}
        </p>
        {isGuest && !study && (
          <p className="mt-2 text-xs text-amber-300" data-testid="daily-guest-note">
            Playing as a guest: today's result and streak are saved on this device only. <Link to="/auth" className="underline">Sign in</Link> to keep them.
          </p>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="panel p-5 flex items-center gap-4">
          <CalendarDays className="w-7 h-7 text-phosphor" />
          <div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground">ITEMS</div>
            <div className="text-2xl font-bold">{data.items.length}</div>
          </div>
        </div>
        <div className="panel p-5 flex items-center gap-4" data-testid="daily-streak">
          <Flame className="w-7 h-7 text-phosphor" />
          <div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground">DAILY STREAK</div>
            <div className="text-2xl font-bold">{data.streak}</div>
          </div>
        </div>
        <div className="panel p-5 flex items-center gap-4">
          <div className="text-phosphor font-extrabold text-2xl">{BASE_POINTS}+</div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground">PER ITEM</div>
            <div className="text-xs text-muted-foreground">plus up to +10 speed bonus</div>
          </div>
        </div>
      </div>
      <button onClick={onStart} data-testid="daily-start-btn" className="bg-primary text-primary-foreground px-8 py-4 text-sm tracking-[0.25em] uppercase font-semibold hover:bg-primary/90">
        {study ? "Start study run →" : "Start today's challenge →"}
      </button>
    </div>
  );
}

function DailyRunner({ items, study = false, onFinish }) {
  const { progress, update } = useProgress();
  const [idx, setIdx] = useState(0);
  const [guess, setGuess] = useState("");
  const [score, setScore] = useState(0);
  const [prog, setProg] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const lockedRef = useRef(false);
  const [mutedState, setMutedState] = useState(isMuted());
  const [audioOk, setAudioOk] = useState(isAudioUnlocked());
  useMuteHotkey(setMutedState);
  const startRef = useRef(Date.now());
  const inputRef = useRef(null);
  const tally = useRef({ correct: 0, missed: [], correctIds: [], missedItems: [] });
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const current = items[idx];
  const isRhythm = current?.type === "rhythm";
  const isTwelve = isRhythm && current.track === "twelve";

  useEffect(() => {
    if (!current) return;
    stopAlarm();
    if (isRhythm && current.lethal && (current.params.pattern === "asystole" || current.params.pattern === "vfib")) startAlarm();
    return () => stopAlarm();
  }, [current, isRhythm]);

  useEffect(() => {
    if (!current || locked) return;
    startRef.current = Date.now();
    setProg(0);
    setTimeout(() => inputRef.current?.focus(), 50);
    if (study) return;
    const tick = setInterval(() => {
      const p = Math.min(1, (Date.now() - startRef.current) / 1000 / ITEM_SECONDS);
      setProg(p);
      if (p >= 1) { clearInterval(tick); settle(false, 1); }
    }, 50);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, locked]);

  const record = (ok) => {
    const p = progressRef.current;
    if (current.type === "med") {
      update({ medication: applyMedResult(p?.medication, current.class, current.id, ok) });
    } else if (!ok) {
      update({ stats: { ...(p?.stats || {}), missed_rhythms: updateMissedRhythms(p?.stats, [current.id], []) } });
    } else {
      update({ stats: { ...(p?.stats || {}), missed_rhythms: updateMissedRhythms(p?.stats, [], [current.id]) } });
    }
  };

  const label = () => (current.type === "med" ? current.generic : current.name);

  const goNext = (next) => {
    if (idx + 1 >= items.length) {
      onFinish({ score: next, correct: tally.current.correct, total: items.length, missed: tally.current.missed, missedItems: tally.current.missedItems });
      return;
    }
    setIdx(idx + 1);
    setGuess("");
    setFeedback(null);
    setRetrying(false);
    lockedRef.current = false;
    setLocked(false);
  };

  // Study: self-paced, retry after a miss, points hidden (still tallied for coins)
  const settleStudy = (ok) => {
    if (lockedRef.current) return;
    if (retrying) {
      if (!ok) { setFeedback({ ok: false, text: `Not quite — it's ${label()}. Type it to lock it in.` }); setGuess(""); return; }
      lockedRef.current = true;
      setLocked(true);
      stopAlarm();
      setFeedback({ ok: true, text: `Locked in • ${label()}` });
      setTimeout(() => goNext(score), 700);
      return;
    }
    if (ok) {
      lockedRef.current = true;
      setLocked(true);
      stopAlarm();
      record(true);
      const next = score + BASE_POINTS;
      tally.current.correct += 1;
      tally.current.correctIds.push(current.id);
      setScore(next);
      setFeedback({ ok: true, text: `Correct • ${label()}` });
      setTimeout(() => goNext(next), 700);
      return;
    }
    record(false);
    tally.current.missed.push(current.id);
    tally.current.missedItems.push(current);
    setRetrying(true);
    setGuess("");
    setFeedback({ ok: false, text: `Not quite — it's ${label()}. Type it to lock it in.` });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const skipStudy = () => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    stopAlarm();
    goNext(score);
  };

  const settle = (ok, atProg) => {
    if (study) return settleStudy(ok);
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    stopAlarm();
    record(ok);
    const name = current.type === "med" ? current.generic : current.name;
    let next = score;
    if (ok) {
      const bonus = BASE_POINTS + Math.round((1 - atProg) * 10);
      next = score + bonus;
      tally.current.correct += 1;
      tally.current.correctIds.push(current.id);
      setFeedback({ ok: true, text: `+${bonus} • ${name}` });
    } else {
      tally.current.missed.push(current.id);
      tally.current.missedItems.push(current);
      setFeedback({ ok: false, text: `${atProg >= 1 ? "Time" : "Missed"} — ${name}` });
    }
    setScore(next);
    setTimeout(() => goNext(next), ok ? 800 : 1700);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!current || !guess.trim() || locked) return;
    settle(fuzzyMatch(guess, current.aliases), prog);
  };

  const handleMute = () => {
    const m = !mutedState;
    setMuted(m);
    setMutedState(m);
    if (m) stopAlarm();
  };

  if (!current) return null;

  return (
    <div data-testid="daily-runner">
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground">// {isRhythm ? "RHYTHM" : "MEDICATION"}{study && <StudyTag testid="daily-run-study-tag" />}</div>
        <div className="flex items-center gap-6">
          {isRhythm && (
            <div data-testid="daily-hr">
              <div className="text-[10px] tracking-widest text-muted-foreground">PULSE</div>
              <div className={`text-2xl font-bold tabular-nums ${isPulseless(current.params) ? "text-destructive" : "text-phosphor"}`}>
                {hrLabel(current.params)} <span className="text-sm text-muted-foreground">bpm</span>
              </div>
            </div>
          )}
          {study ? (
            <div data-testid="daily-missed-count">
              <div className="text-[10px] tracking-widest text-muted-foreground">TO REVIEW</div>
              <div className={`text-2xl font-bold ${tally.current.missed.length ? "text-destructive" : ""}`}>{tally.current.missed.length}</div>
            </div>
          ) : (
          <div data-testid="daily-score">
            <div className="text-[10px] tracking-widest text-muted-foreground">SCORE</div>
            <div className="text-2xl font-bold text-phosphor">{score}</div>
          </div>
          )}
          <div data-testid="daily-counter">
            <div className="text-[10px] tracking-widest text-muted-foreground">ITEM</div>
            <div className="text-2xl font-bold">{idx + 1}<span className="text-sm text-muted-foreground"> / {items.length}</span></div>
          </div>
          <button onClick={handleMute} data-testid="daily-mute-toggle" title="Toggle monitor audio (M)" aria-label="Toggle monitor audio, hotkey M" className="relative p-2 border border-phosphor/30 hover:border-phosphor transition-colors">
            <span className="absolute -top-2 -right-2 text-[8px] px-1 border border-border bg-background text-muted-foreground" aria-hidden>M</span>
            {mutedState ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-phosphor" />}
          </button>
        </div>
      </div>

      {!audioOk && (
        <button onClick={async () => setAudioOk(await unlockAudio())} data-testid="daily-enable-audio" className="w-full mb-3 py-2 border border-phosphor/40 text-xs tracking-widest text-phosphor hover:bg-primary/10">
          TAP TO ENABLE MONITOR AUDIO
        </button>
      )}

      {!study && (
      <div className="h-1 bg-border mb-3 overflow-hidden" data-testid="daily-timer">
        <div className={`h-full transition-[width] duration-75 ${prog > 0.75 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${(1 - prog) * 100}%` }} />
      </div>
      )}

      <div className="panel scanlines p-2 relative" data-testid="daily-stage">
        {isRhythm ? (
          isTwelve ? <TwelveLeadGrid params={current.params} onBeat={qrsBeep} /> : <MonitorStrip params={current.params} onBeat={qrsBeep} className="h-64 sm:h-72" />
        ) : (
          <div className="min-h-[260px] flex flex-col items-center justify-center text-center px-6 py-10">
            <div className="text-[10px] tracking-[0.4em] text-muted-foreground mb-4">CLUE</div>
            <div className="text-lg sm:text-2xl leading-snug max-w-2xl" data-testid="daily-clue">{current.clue}</div>
          </div>
        )}
        {feedback && (
          <div data-testid="daily-feedback" className={`absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 border bg-background/90 text-sm font-semibold tracking-widest ${feedback.ok ? "border-phosphor text-phosphor" : "border-destructive text-destructive"}`}>
            {feedback.text}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="mt-5 flex gap-3" data-testid="daily-form">
        <input
          ref={inputRef}
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder={isRhythm ? 'Type the rhythm (e.g. "afib")' : "Type the medication name..."}
          data-testid="daily-input"
          className="flex-1 bg-transparent border border-phosphor/30 focus:border-phosphor focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono"
          autoComplete="off"
          disabled={locked}
        />
        <button type="submit" disabled={locked} data-testid="daily-submit" className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] font-semibold hover:bg-primary/90 disabled:opacity-50">
          ENTER
        </button>
        {study && retrying && (
          <button type="button" onClick={skipStudy} disabled={locked} data-testid="daily-skip-btn" className="border border-border px-4 py-3 text-xs tracking-widest text-muted-foreground hover:text-foreground hover:border-phosphor/60 disabled:opacity-50">
            SKIP →
          </button>
        )}
      </form>
    </div>
  );
}
